import { OverlayContainer, OverlayRef } from "@angular/cdk/overlay";
import { ComponentPortal, PortalInjector } from "@angular/cdk/portal";
import { Injectable, Type } from "@angular/core";
import {
	MatLegacyDialog as MatDialog,
	MatLegacyDialogConfig as MatDialogConfig,
	MatLegacyDialogContainer as MatDialogContainer,
	MatLegacyDialogRef as MatDialogRef,
} from "@angular/material/legacy-dialog";
import { Store } from "@ngrx/store";
import {
	CoreState,
	DialogService as AbstractDialogService,
	DialogStatusAction,
	NativeFeaturesConfigService,
} from "@obg/ngx.core";
import { DeviceConfigService, PlatformService } from "@obg/ngx.global";
import { RouteAction, RouteSelector } from "@obg/ngx.router";
import { castArray, includes, isEmpty } from "lodash-es";
import { EMPTY, Observable, of, Subject } from "rxjs";
import { mergeMap, take, tap } from "rxjs/operators";

import { SlideUpMatDialogContainer } from "../dialog/container/slide-up-mat-dialog.container";
import { IframeDialogLayoutComponent } from "../dialog-layout/iframe-dialog-layout/iframe-dialog-layout.component";

import { NativeMatDialogContainer } from "./container/native-mat-dialog.container";
import { DialogLazyComponent } from "./dialog-lazy/dialog-lazy.component";
import { DialogLikeContainer } from "./dialog-like/dialog-like.container";
import { DialogRouteContainer } from "./dialog-route/dialog-route.container";
import { DialogComponentData, DialogConfig, DialogLike } from "./dialog.model";

export const DIALOG_PANEL_CLASS = "obg-mat-dialog-panel";

// todo: OBGD-1877 - refactor dialog service
@Injectable({
	providedIn: "root",
})
export class DialogService extends AbstractDialogService {
	private routeConfigs: Record<string, DialogConfig> = {};
	private componentConfigs = new Map<Type<any>, DialogConfig>();
	private openDialogId = "";
	private openDialog$ = new Subject<MatDialogRef<any>>();
	private readonly isNativeEnabled: boolean;

	get openedDialog$(): Observable<MatDialogRef<any>> {
		return this.openDialog$;
	}

	constructor(
		private dialog: MatDialog,
		private store: Store<CoreState>,
		private dialogStatusAction: DialogStatusAction,
		private platformService: PlatformService,
		private overlayContainer: OverlayContainer,
		nativeFeaturesConfigService: NativeFeaturesConfigService,
		private deviceConfigService: DeviceConfigService
	) {
		super();
		this.isNativeEnabled = nativeFeaturesConfigService.getEnabled();
		/**
		 * Monkey patch for `MatDialog` until angular material does not support custom containers.
		 * https://github.com/angular/material2/issues/8857
		 */
		(MatDialog as any).prototype._attachDialogContainer = function (
			overlay: OverlayRef,
			config: DialogConfig
		): MatDialogContainer {
			const userInjector = config?.viewContainerRef?.injector;
			const injector = new PortalInjector(
				userInjector || (this as any)._injector,
				new WeakMap([[MatDialogConfig, config]])
			);
			const containerPortal = new ComponentPortal(
				config.containerComponent || MatDialogContainer,
				config.viewContainerRef,
				injector
			);
			const containerRef = overlay.attach<MatDialogContainer>(containerPortal);

			return containerRef.instance;
		};
	}

	openComponent<T>(component: Type<T>, dialogConfig?: DialogConfig): MatDialogRef<any, any>;

	openComponent<T>(component: DialogLike<T>, dialogConfig?: DialogConfig): MatDialogRef<any, any>;

	openComponent<T>(component: DialogLike<any> | Type<T>, dialogConfig?: DialogConfig): MatDialogRef<any, any> {
		let dialogId: string | undefined;
		let host: Type<any>;
		let componentToShow: Type<any>;
		let dialogData = dialogConfig?.data;

		if (this.isDialogLike(component)) {
			dialogId = component.dialogId;
			componentToShow = component.dialogComponentType;
			host = DialogLikeContainer;
			dialogData = { ...dialogConfig?.data, dialogLike: component };
		} else {
			dialogId = dialogConfig?.id;
			componentToShow = component;
			host = DialogLazyComponent;
			dialogData = { ...dialogConfig?.data, dialogLazyComponent: component };
		}

		return this.openDialog(host, {
			...this.componentConfigs.get(componentToShow),
			...dialogConfig,
			id: dialogId,
			data: dialogData,
		});
	}

	openComponentFullscreen<T>(component: DialogLike<T>, data?: any): MatDialogRef<any, any> {
		return this.openComponent<T>(component, {
			containerComponent: this.isNativeEnabled ? NativeMatDialogContainer : SlideUpMatDialogContainer,
			panelClass: "obg-m-dialog-fullscreen",
			closeOnNavigation: false,
			data: { ...data, hideClose: true, hideFooter: true },
			disableClose: true,
		});
	}

	openRoute(
		routeName: string,
		dialogConfig?: DialogConfig
	): Observable<MatDialogRef<DialogRouteContainer>> | undefined {
		if (this.platformService.isServer) {
			return undefined;
		}

		const config = { ...this.routeConfigs[routeName], ...dialogConfig };

		return this.store.select(RouteSelector.getCurrent).pipe(
			take(1),
			mergeMap((currentRoute) => {
				if (currentRoute) {
					if (currentRoute.name === routeName) {
						return EMPTY;
					}

					if (includes(config.noDialogForRoute, currentRoute.name)) {
						this.store.dispatch(RouteAction.navigate(routeName));
						return EMPTY;
					}
				}

				let dialogRef: MatDialogRef<DialogRouteContainer> | undefined;

				if (!isEmpty(this.openDialogId)) {
					dialogRef = this.dialog.getDialogById(this.openDialogId);

					if (dialogRef) {
						dialogRef.close();
					}
				}

				const showDialog$ = dialogRef ? dialogRef.afterClosed() : of(undefined);
				return showDialog$.pipe(
					take(1),
					tap(() => {
						this.openDialogId = routeName;
						this.openDialog(DialogRouteContainer, {
							id: routeName,
							...(config as MatDialogConfig),
							data: {
								...config?.data,
								routeName,
							} as DialogComponentData,
						});
					})
				);
			})
		);
	}

	registerRouteConfig(routeName: string, config: DialogConfig) {
		this.routeConfigs[routeName] = config;
	}

	registerComponentConfig(component: Type<any>, config: DialogConfig) {
		this.componentConfigs.set(component, config);
	}

	openLinkInPopup(url: string, title: string) {
		const isDesktop = this.deviceConfigService.isDesktop();

		const dialogConfig: Partial<DialogConfig> = {
			data: {
				src: url,
				titleText: title,
			},
			width: isDesktop ? "80dvw" : "100dvw",
			height: isDesktop ? "80dvh" : "100dvh",
		};

		this.openComponent(IframeDialogLayoutComponent, dialogConfig);
	}

	private isDialogLike(component: DialogLike<any> | Type<any>): component is DialogLike<any> {
		return component?.hasOwnProperty("dialogComponentType") && component.hasOwnProperty("dialogId");
	}

	private openDialog(host: Type<any>, config: MatDialogConfig): MatDialogRef<any> {
		const dialog = this.dialog.open(host, {
			...config,
			panelClass: this.getPanelClass(config),
		});

		// todo: remove once https://github.com/angular/material2/issues/10530 is fixed
		this.platformService
			.forClient(() => dialog.afterOpened())
			// eslint-disable-next-line rxjs/no-ignored-subscription
			.subscribe(() => {
				const element = this.overlayContainer.getContainerElement();
				element.style.cssText += ";-webkit-transform: rotateZ(0deg) !important";
				element.style.cssText += ";-webkit-transform: none !important";
			});

		return this.trackDialog(dialog);
	}

	trackDialog(dialog: MatDialogRef<any>): MatDialogRef<any> {
		this.store.dispatch(this.dialogStatusAction.open());
		this.openDialog$.next(dialog);
		dialog
			.afterClosed()
			.pipe(
				take(1),
				tap(() => this.store.dispatch(this.dialogStatusAction.close()))
			)
			.subscribe(); // eslint-disable-line rxjs/no-ignored-subscription

		return dialog;
	}

	private getPanelClass(config: DialogConfig | undefined): string[] {
		if (!config || !config.panelClass) {
			return [DIALOG_PANEL_CLASS];
		}

		return [DIALOG_PANEL_CLASS, ...castArray(config.panelClass)];
	}
}
